import React from 'react';
import { ChevronLeftIcon, CheckCircleIcon, FastForwardIcon } from '@heroicons/react/solid';

export function DocumentReviewHeader({
  title,
  reviewedCodes,
  totalParagraphs,
  isSaving,
  handleExitReview,
  goToFirstUnreviewed,
  calculateProgress,
  showTemplateContext,
  onToggleTemplateContext,
}) {
  return (
    <div className="flex items-center justify-between mb-4 px-8">
       {/* Left side with Save & Exit button */}
       <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleExitReview}
          disabled={isSaving}
          className={`font-semibold text-sm flex items-center py-2 px-4 rounded-lg shadow-sm transition-all duration-300 text-gray-900 border border-gray-300
            ${isSaving ? 'bg-gray-400 cursor-not-allowed text-white' : ''}`}
        >
          {isSaving ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Saving Approvals...
            </>
          ) : (
            <>              
              <ChevronLeftIcon className="w-5 h-5 mr-1" aria-hidden="true" />
              <span>Save & Exit</span>
            </>
          )}
        </button>
      </div>

      {/* Center section with title */}
      <div className="flex-grow text-center">
        <h1 className="text-xl font-bold text-slate-800">
          {title}
        </h1>
      </div>

      {/* Right side with template toggle, progress info, and continue/complete button */}
      <div className="font-medium flex items-center gap-3 flex-shrink-0">
        {/* "Show in report template" toggle – only rendered when the
            parent passes the callback (i.e. on the PR v2 page) */}
        {onToggleTemplateContext && (
          <label className="flex items-center gap-2 cursor-pointer select-none mr-2">
            <span className="text-xs text-gray-500 whitespace-nowrap">Show in template</span>
            <button
              type="button"
              role="switch"
              aria-checked={showTemplateContext}
              onClick={onToggleTemplateContext}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200
                ${showTemplateContext ? 'bg-violet-600' : 'bg-gray-300'}`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform duration-200
                  ${showTemplateContext ? 'translate-x-[18px]' : 'translate-x-[3px]'}`}
              />
            </button>
          </label>
        )}            
        <div className="text-slate-700 whitespace-nowrap">
          {totalParagraphs - reviewedCodes.size === 0
            ? `All ${totalParagraphs} sections approved`
            : `${totalParagraphs - reviewedCodes.size} left to review`
          }
        </div>
        
        {/* Progress bar */}
        <div className="w-72 h-2 bg-gray-200 rounded-full relative overflow-hidden">
          <div
            className={`absolute top-0 left-0 h-full rounded-full transition-width duration-300 
              ${calculateProgress() === 100 ? 'bg-green-600' : 'bg-violet-600'}`}
            style={{ width: `${calculateProgress()}%` }}
          />
        </div>
        <div className="text-slate-700 leading-tight min-w-[30px]">
          {calculateProgress()}%
        </div>

        <button
          onClick={reviewedCodes.size === totalParagraphs ? handleExitReview : goToFirstUnreviewed}
          disabled={isSaving}
          className={`font-semibold flex items-center px-4 py-2 text-sm rounded-lg
            ${isSaving ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-violet-800 hover:bg-violet-700 text-white'}`}
        >
          {isSaving ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Saving Approvals...
            </>
          ) : reviewedCodes.size === totalParagraphs ? (
            <>
              <CheckCircleIcon className="w-5 h-5 mr-1" aria-hidden="true" />
              <span>Complete Review</span>
            </>
          ) : (
            <>
              {reviewedCodes.size === 0 ? 'Start Review' : 'Continue Review'}
              <FastForwardIcon className="w-5 h-5 ml-1" aria-hidden="true" />
            </>
          )}
        </button>
      </div>
    
        
    </div>
  );
}
export default DocumentReviewHeader;